import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
    AccessSummaryDto,
    MyActionItemDto,
    NextUpDto,
    ResourceListItemDto,
    ResourceType,
    SearchResponseDto,
    UpdateAccessDto,
} from '../../../shared/types/access';
import { api, shouldRetry } from './api';

/** Home lists: my active spaces, spaces shared with me, my archived spaces. */
export type ResourceScope = 'owned' | 'shared' | 'archived';

export const platformKeys = {
    all: ['resources'] as const,
    resources: (scope: ResourceScope) => ['resources', scope] as const,
    search: (q: string) => ['resources', 'search', q] as const,
    myActions: ['my-actions'] as const,
    access: (type: ResourceType, shareId: string) => ['access', type, shareId] as const,
};

/** Home page lists. */
export function useResources(scope: ResourceScope, enabled = true) {
    return useQuery({
        queryKey: platformKeys.resources(scope),
        queryFn: async () => (await api.get<ResourceListItemDto[]>('/api/me/resources', { params: { scope } })).data,
        enabled,
        staleTime: 30_000,
    });
}

/** Title search over my spaces (archived included) and spaces shared with me. Keeps the last results while typing. */
export function useResourceSearch(query: string) {
    const q = query.trim();
    return useQuery({
        queryKey: platformKeys.search(q),
        queryFn: async ({ signal }) => (await api.get<ResourceListItemDto[]>('/api/me/resources/search', { params: { q }, signal })).data,
        enabled: q.length > 0,
        placeholderData: previous => previous,
        staleTime: 15_000,
    });
}

/** Full search (titles and content) for the results page. `type` narrows to one tool. */
export function useFullSearch(query: string, type: ResourceType | null) {
    const q = query.trim();
    return useQuery({
        queryKey: ['search', q, type ?? 'all'],
        queryFn: async ({ signal }) => (await api.get<SearchResponseDto>('/api/search', { params: { q, ...(type ? { type } : {}) }, signal })).data,
        enabled: q.length > 0,
        placeholderData: previous => previous,
        staleTime: 15_000,
    });
}

/** The caller's role on a resource and its tier/limits. Errors are AccessErrorBody (see toAccessError). */
export function useAccess(type: ResourceType, shareId: string | undefined) {
    return useQuery({
        queryKey: platformKeys.access(type, shareId ?? ''),
        queryFn: async () => (await api.get<AccessSummaryDto>(`/api/access/${type}/${shareId}`)).data,
        enabled: !!shareId,
        retry: shouldRetry,
        staleTime: 30_000,
    });
}

export function useUpdateAccess(type: ResourceType, shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (dto: UpdateAccessDto) => (await api.patch<AccessSummaryDto>(`/api/access/${type}/${shareId}`, dto)).data,
        onSuccess: data => qc.setQueryData(platformKeys.access(type, shareId), data),
    });
}

/** Facilitator: send everyone in this space to another of their spaces (everyone hears it live as next_up). */
export function useSetNextUp(type: ResourceType, shareId: string) {
    return useMutation({
        mutationFn: async (target: { type: ResourceType; shareId: string }) =>
            (await api.put<NextUpDto>(`/api/access/${type}/${shareId}/next`, target)).data,
    });
}

export function useClearNextUp(type: ResourceType, shareId: string) {
    return useMutation({
        mutationFn: async () => { await api.delete(`/api/access/${type}/${shareId}/next`); },
    });
}

/** Reset the share link. Resolves with the new summary; the caller should navigate to the new shareId. */
export function useRotateLink(type: ResourceType, shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => (await api.post<AccessSummaryDto>(`/api/access/${type}/${shareId}/rotate`)).data,
        onSuccess: data => {
            qc.setQueryData(platformKeys.access(type, data.shareId), data);
            qc.invalidateQueries({ queryKey: platformKeys.all });
        },
    });
}

export function useForgetShared() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ type, shareId }: { type: ResourceType; shareId: string }) =>
            api.delete(`/api/me/resources/shared/${type}/${shareId}`),
        onSuccess: () => qc.invalidateQueries({ queryKey: platformKeys.resources('shared') }),
    });
}

/** Home's "My actions": open action items assigned to me on boards and retros I can open. */
export function useMyActions(enabled = true) {
    return useQuery({
        queryKey: platformKeys.myActions,
        queryFn: async () => (await api.get<MyActionItemDto[]>('/api/me/action-items')).data,
        enabled,
        staleTime: 30_000,
    });
}

/** Tick an action item off (or back on) where it lives: its board or retro. */
export function useSetActionDone() {
    return useMutation({
        mutationFn: async ({ item, completed }: { item: MyActionItemDto; completed: boolean }) => {
            const path = `/${encodeURIComponent(item.shareId)}/action-items/${encodeURIComponent(item.id)}`;
            if (item.type === 'retro') await api.put(`/api/retros${path}`, { completed });
            else await api.patch(`/api/boards${path}`, { completed });
        },
    });
}

export function useDeleteAccount() {
    return useMutation({
        mutationFn: async () => api.delete('/api/me'),
    });
}
